import { useMemo } from 'react';
import { ROLE_LEVEL, effectivePermissions, sectionKeys, type PermissionKey } from '@supernova/shared';
import { useAuthStore } from '@/stores/auth-store';

/**
 * The keys that open each sidebar tab: any one of them will do. A tab's
 * screens and buttons then check their own keys.
 */
export const TAB_PERMISSIONS = {
  users: ['users.view'],
  receivePayments: sectionKeys('receive-payments'),
  posManagement: sectionKeys('pos-management'),
  sendPayments: sectionKeys('send-payments'),
  reports: sectionKeys('reports'),
  transactions: ['transactions.view'],
  reconciliation: ['reconciliation.card.view', 'reconciliation.cash.view'],
  statements: ['statements.view'],
  shelfLabels: ['esl.labels.view', 'esl.gateways.view'],
} as const satisfies Record<string, readonly PermissionKey[]>;

export type PermissionTab = keyof typeof TAB_PERMISSIONS;

/** Any one of these opens POS Management; its tabs are then filtered one by one. */
export const POS_MANAGEMENT_PERMISSIONS: readonly PermissionKey[] = TAB_PERMISSIONS.posManagement;

/** Role levels for the few screens that stay Owner or Admin only (Settings, POS Settings, …). */
export type RoleTier = 'owner' | 'admin' | 'manager';

/**
 * The signed-in user's dashboard permissions, as worked out by auth: their role
 * (built-in or custom) plus any extras granted on the Users page. Holding an
 * action counts as holding its screen's View.
 *
 * `atLeast` is for what stays tied to the role rather than a key: Settings,
 * POS and Mobile Settings, Roles, owner-only settings tabs.
 */
export function usePermissions() {
  const { user } = useAuthStore();
  const role = user?.role;
  const extras = user?.extraPermissions;
  const signed = user?.permissions;
  return useMemo(() => {
    const perms = effectivePermissions(role, extras, signed);
    const level = ROLE_LEVEL[role ?? ''] ?? 0;
    return {
      has: (key: PermissionKey) => perms.has(key),
      hasAny: (...keys: readonly PermissionKey[]) => keys.some((k) => perms.has(k)),
      /** The tab opens: any of its keys is held. */
      canOpen: (tab: PermissionTab) => TAB_PERMISSIONS[tab].some((k) => perms.has(k)),
      /** The role is this tier or above (super admins always are). */
      atLeast: (tier: RoleTier) => level >= ROLE_LEVEL[tier],
    };
  }, [role, extras, signed]);
}

export type Permissions = ReturnType<typeof usePermissions>;
